import { useState, useRef, useEffect } from "react";
import { Search, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface Option {
  id: number;
  name: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: number;
  valueName: string;
  onChange: (id: number, name: string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  required?: boolean;
}

export function SearchableSelect({
  options,
  value,
  valueName,
  onChange,
  placeholder = "Secin...",
  disabled = false,
  loading = false,
  label,
  required = false,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = query
    ? options.filter((o) =>
        o.name.toLocaleLowerCase("tr").includes(query.toLocaleLowerCase("tr"))
      )
    : options;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (opt: Option) => {
    onChange(opt.id, opt.name);
    setIsOpen(false);
    setQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(0, "");
    setQuery("");
  };

  const handleOpen = () => {
    if (disabled || loading) return;
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div>
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-foreground">
          {label}
          {required && " *"}
        </label>
      )}
      <div ref={containerRef} className="relative">
        {/* Trigger */}
        <button
          type="button"
          onClick={handleOpen}
          disabled={disabled || loading}
          className={cn(
            "h-9 w-full flex items-center justify-between rounded-lg border border-border bg-secondary px-3 text-sm transition-all text-left",
            "focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            isOpen && "border-accent ring-1 ring-accent",
            !value ? "text-muted-foreground" : "text-foreground"
          )}
        >
          <span className="truncate">
            {loading ? "Yukleniyor..." : value ? valueName : placeholder}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {value > 0 && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === "Enter")
                    handleClear(e as unknown as React.MouseEvent);
                }}
                className="rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors cursor-pointer"
              >
                <X className="size-3.5" />
              </span>
            )}
            <ChevronDown
              className={cn(
                "size-4 text-muted-foreground transition-transform",
                isOpen && "rotate-180"
              )}
            />
          </div>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-card shadow-2xl shadow-black/50 ring-1 ring-white/5 overflow-hidden">
            {/* Search */}
            <div className="flex items-center gap-2 border-b border-border px-3 py-2">
              <Search className="size-4 text-muted-foreground shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ara..."
                className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
              />
            </div>

            {/* Options */}
            <div className="max-h-52 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="px-4 py-4 text-center text-sm text-muted-foreground">
                  Sonuc bulunamadi
                </div>
              ) : (
                filtered.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => handleSelect(opt)}
                    className={cn(
                      "w-full px-4 py-2 text-left text-sm transition-colors hover:bg-accent/10 hover:text-foreground",
                      opt.id === value
                        ? "bg-accent/10 text-accent font-medium"
                        : "text-foreground"
                    )}
                  >
                    {opt.name}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
